/* The header search. The prototype's `#gq` — one box across every register,
   grouped results, keyboard-driven.

   It reads the same query keys the screens do, so it is served from the TanStack
   cache and from IndexedDB when offline. It never fetches anything of its own:
   if a register has not been opened yet this session the persister has it from
   the last one, and if it genuinely has never been read it simply does not
   appear in the results. */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { visibleLicences } from '../lib/registers';
import { qk } from '../query/keys';
import { useServices } from '../services/context';
import s from './AppShell.module.scss';

interface Hit {
  group: string;
  icon: string;
  title: string;
  sub: string;
  to: string;
}

const MAX_PER_GROUP = 4;

export function GlobalSearch() {
  const svc = useServices();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const groups = useQuery({ queryKey: qk.groups, queryFn: () => svc.groups.all() });
  const skus = useQuery({ queryKey: qk.licences, queryFn: () => svc.licences.skus() });
  const applications = useQuery({ queryKey: qk.applications, queryFn: () => svc.applications.list() });
  const suppliers = useQuery({ queryKey: qk.suppliers, queryFn: () => svc.suppliers.list() });
  const domains = useQuery({ queryKey: qk.domains, queryFn: () => svc.domains.list() });
  const articles = useQuery({ queryKey: qk.articles, queryFn: () => svc.articles.list() });
  const credentials = useQuery({ queryKey: qk.credentials, queryFn: () => svc.credentials.list() });

  /* Ctrl/⌘-K, the shortcut anyone who uses a search box expects to work. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        input.current?.focus();
        input.current?.select();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const hits = useMemo<Hit[]>(() => {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return [];
    const has = (...parts: (string | number | undefined | null)[]) =>
      parts.some((p) => String(p ?? '').toLowerCase().includes(needle));

    const out: Hit[] = [];
    const push = (rows: Hit[]) => out.push(...rows.slice(0, MAX_PER_GROUP));

    push(
      (people.data ?? [])
        .filter((p) => has(p.name, p.upn, p.email ?? '', p.title, p.dept))
        .map((p) => ({
          group: 'Accounts',
          icon: 'ti-user',
          title: p.name,
          sub: `${p.title} · ${p.dept}`,
          /* Straight to the record now that every one has a URL, rather than to
             the register for you to find them again. */
          to: `/people?open=${encodeURIComponent(p.id)}`,
        })),
    );
    push(
      (devices.data ?? [])
        .filter((d) => has(d.tag, d.make, d.model, d.serial, d.ip, d.os))
        .map((d) => ({
          group: 'Devices',
          icon: 'ti-device-laptop',
          title: d.tag,
          sub: `${d.make} ${d.model} · ${d.serial}`,
          to: `/devices?open=${encodeURIComponent(d.id)}`,
        })),
    );
    push(
      (groups.data ?? [])
        .filter((g) => has(g.name, g.desc, g.purpose, g.email))
        .map((g) => ({
          group: 'Groups',
          icon: 'ti-users-group',
          title: g.name,
          sub: g.purpose || g.desc || `${g.memberCount} members`,
          to: `/groups?open=${encodeURIComponent(g.id)}`,
        })),
    );
    push(
      visibleLicences(skus.data ?? [])
        .filter((k) => has(k.name, k.short))
        .map((k) => ({
          group: 'Licences',
          icon: 'ti-license',
          title: k.short || k.name,
          sub: `${k.total} prepaid`,
          to: `/licences?open=${encodeURIComponent(k.id)}`,
        })),
    );
    push(
      (applications.data ?? [])
        .filter((a) => has(a.name, a.purpose, a.supplier))
        .map((a) => ({ group: 'Applications', icon: 'ti-apps', title: a.name, sub: a.purpose, to: '/applications' })),
    );
    push(
      (suppliers.data ?? [])
        .filter((v) => has(v.name, v.service, v.acct))
        .map((v) => ({
          group: 'Suppliers',
          icon: 'ti-building-store',
          title: v.name,
          sub: `${v.service} · acct ${v.acct}`,
          to: '/suppliers',
        })),
    );
    push(
      (domains.data ?? [])
        .filter((d) => has(d.name, d.reg))
        .map((d) => ({ group: 'Domains', icon: 'ti-world', title: d.name, sub: `Registrar ${d.reg}`, to: '/domains' })),
    );
    push(
      (articles.data ?? [])
        .filter((a) => has(a.title, a.desc, a.cat))
        .map((a) => ({ group: 'Knowledge base', icon: a.icon, title: a.title, sub: a.cat, to: '/knowledge-base' })),
    );
    push(
      (credentials.data ?? [])
        .filter((c) => has(c.name, c.system, c.type))
        .map((c) => ({ group: 'Credentials', icon: 'ti-key', title: c.name, sub: c.system, to: '/credentials' })),
    );

    return out.slice(0, 20);
  }, [
    q,
    people.data,
    devices.data,
    groups.data,
    skus.data,
    applications.data,
    suppliers.data,
    domains.data,
    articles.data,
    credentials.data,
  ]);

  const choose = (hit: Hit) => {
    setOpen(false);
    setQ('');
    navigate(hit.to);
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!hits.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => (c + 1) % hits.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => (c - 1 + hits.length) % hits.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(hits[cursor] ?? hits[0]);
    }
  };

  let lastGroup = '';

  return (
    <div className={s.gsearch} ref={box}>
      <i className="ti ti-search" aria-hidden />
      <input
        ref={input}
        type="text"
        value={q}
        placeholder="Search anything — people, kit, subnets, contracts…"
        aria-label="Search everything"
        autoComplete="off"
        onChange={(e) => {
          setQ(e.target.value);
          setCursor(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />

      {open && q.trim().length >= 2 && (
        <div className={s.gres} role="listbox">
          {hits.length === 0 ? (
            <div className={s.gresEmpty}>Nothing matches “{q.trim()}”.</div>
          ) : (
            hits.map((hit, i) => {
              const head = hit.group !== lastGroup ? hit.group : null;
              lastGroup = hit.group;
              return (
                <div key={`${hit.to}-${hit.title}-${i}`}>
                  {head && <div className={s.gresHead}>{head}</div>}
                  <button
                    type="button"
                    className={s.gresItem}
                    data-active={i === cursor}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => choose(hit)}
                  >
                    <span className={s.gresIcon}>
                      <i className={`ti ${hit.icon}`} aria-hidden />
                    </span>
                    <span className={s.min0}>
                      <b>{hit.title}</b>
                      <small>{hit.sub}</small>
                    </span>
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
